import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  ExpenseListResponseSchema,
  ExpenseSchema,
  ImportExpensesResultSchema,
  type CreateExpenseInput,
  type Expense,
  type ExpenseCategory,
  type ImportExpensesInput,
  type ImportExpensesResult,
  type PatchExpenseInput,
} from "@/api/types/expense";
import { financeKeys } from "./useFinance";

/** Query-key factory — mirrors useInvoices'/useReviews' convention (cursor list + detail). */
export const expenseKeys = {
  all: ["expenses"] as const,
  lists: () => [...expenseKeys.all, "list"] as const,
  list: (params: { from?: string; to?: string; category?: ExpenseCategory; limit?: number }) =>
    [...expenseKeys.lists(), params] as const,
  details: () => [...expenseKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...expenseKeys.details(), id] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: expenseKeys.all });
  // Expenses feed the finance summary/insights totals — any expense mutation
  // (create/patch/delete/import) has to bust those too or the Cash Flow /
  // Insights tabs silently go stale.
  void queryClient.invalidateQueries({ queryKey: financeKeys.all });
}

interface UseExpensesParams {
  from?: string;
  to?: string;
  category?: ExpenseCategory;
  limit?: number;
}

/** List query with cursor pagination, mirroring useInvoices'/useReviews' convention. */
export function useExpenses(params: UseExpensesParams = {}) {
  const { from, to, category, limit = 25 } = params;

  return useInfiniteQuery({
    queryKey: expenseKeys.list({ from, to, category, limit }),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (from) searchParams.set("from", from);
      if (to) searchParams.set("to", to);
      if (category) searchParams.set("category", category);
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(`/v2/expenses?${searchParams.toString()}`);
      return validateResponse(ExpenseListResponseSchema, data, "GET /v2/expenses");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 15 * 1000,
  });
}

export function useExpense(id: string | undefined) {
  return useQuery<Expense>({
    queryKey: expenseKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/expenses/${id}`);
      return validateResponse(ExpenseSchema, data, "GET /v2/expenses/:id");
    },
    enabled: !!id,
  });
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  return useMutation<Expense, unknown, CreateExpenseInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/expenses", input);
      return validateResponse(ExpenseSchema, data, "POST /v2/expenses");
    },
    onSuccess: (expense) => {
      queryClient.setQueryData(expenseKeys.detail(expense.id), expense);
      invalidateAll(queryClient);
    },
  });
}

export function usePatchExpense() {
  const queryClient = useQueryClient();
  return useMutation<Expense, unknown, PatchExpenseInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/expenses/${id}`, patch);
      return validateResponse(ExpenseSchema, data, "PATCH /v2/expenses/:id");
    },
    onSuccess: (expense) => {
      queryClient.setQueryData(expenseKeys.detail(expense.id), expense);
      invalidateAll(queryClient);
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/v2/expenses/${id}`);
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}

/**
 * Bank-import batch: the CSV is read client-side (FileReader on an uploaded
 * .csv, or a pasted textarea — see FinancesPage's ImportPanel) and posted as
 * raw text, never parsed in the browser. The API does the parsing/dedup/
 * categorization and returns the batch result for display.
 */
export function useImportExpenses() {
  const queryClient = useQueryClient();
  return useMutation<ImportExpensesResult, unknown, ImportExpensesInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/expenses/import", input);
      return validateResponse(
        ImportExpensesResultSchema,
        data,
        "POST /v2/expenses/import",
      );
    },
    onSuccess: () => invalidateAll(queryClient),
  });
}
